'use client'

import React, { useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@material-tailwind/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faLink, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { ParsedIngredient } from '@/lib/recipe-import/types'
import IngredientMatchingModal from './IngredientMatchingModal'

interface RecipeImportModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (scrapedData: ScrapedRecipeData) => void
}

interface ScrapedRecipeData {
  title: string
  ingredients: string[]
  parsedIngredients: ParsedIngredient[]
  instructions: string[]
  prepTime?: number
  cookTime?: number
  totalTime?: number
  servings?: string
  image?: string
  sourceUrl: string
}

const RecipeImportModal: React.FC<RecipeImportModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showIngredientModal, setShowIngredientModal] = useState(false)
  const [scrapedData, setScrapedData] = useState<ScrapedRecipeData | null>(null)

  const validateUrl = (urlString: string): boolean => {
    try {
      const urlObj = new URL(urlString)
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  const handleSubmit = async () => {
    setError(null)

    // Validate URL
    if (!url.trim()) {
      setError('Please enter a recipe URL')
      return
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL (must start with http:// or https://)')
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/recipes/scrape-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to scrape recipe')
      }

      // Success! Go directly to form
      onSuccess(data.data)
      setUrl('')
      onClose()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'

      // Provide helpful error messages for common scenarios
      if (errorMessage.includes('unsupported') || errorMessage.includes('not supported')) {
        setError(
          "Hmm, we couldn't grab that one! Try pasting the direct link to the recipe page (not a Pinterest or social media link), or add it manually — it only takes a minute."
        )
      } else if (errorMessage.includes('timeout')) {
        setError('Request timed out. Please try again.')
      } else if (errorMessage.includes('network') || errorMessage.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setUrl('')
      setError(null)
      setShowIngredientModal(false)
      setScrapedData(null)
      onClose()
    }
  }

  const handleIngredientConfirm = (confirmedIngredients: ParsedIngredient[]) => {
    if (scrapedData) {
      // Update scraped data with confirmed ingredients
      const updatedData = {
        ...scrapedData,
        parsedIngredients: confirmedIngredients,
      }
      onSuccess(updatedData)
      setUrl('')
      setError(null)
      setShowIngredientModal(false)
      setScrapedData(null)
      onClose()
    }
  }

  const handleIngredientModalClose = () => {
    setShowIngredientModal(false)
    // Keep the import modal open so user can try again
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[500px] bg-white p-6 font-inter sm:max-w-[500px] xs:max-w-[calc(100vw-2rem)] xs:mx-4">
        <div className="space-y-4">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Import Recipe from URL</h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              Paste a direct link to the recipe page
            </p>
          </div>

          {/* URL Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Recipe URL
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <FontAwesomeIcon icon={faLink} className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value)
                  setError(null)
                }}
                onKeyPress={handleKeyPress}
                placeholder="https://www.allrecipes.com/recipe/..."
                className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-[#85C349] focus:outline-none focus:ring-2 focus:ring-[#85C349] focus:ring-opacity-50"
                disabled={loading}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800">
                    Unable to Import Recipe
                  </h3>
                  <p className="mt-1 text-sm text-red-700 whitespace-pre-line">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-5 w-5 text-blue-500 animate-spin"
                />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Importing recipe...
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    This may take a few seconds
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Supported Sites Info */}
          {!error && !loading && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
              <p className="text-xs text-gray-600">
                <strong className="text-gray-700">Tip:</strong> Use the direct link to the recipe page — not a Pinterest, Instagram, or social media link.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col xs:flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !url.trim()}
              className="flex-1 rounded-full bg-[#85C349] px-6 py-3 text-sm font-medium text-white hover:bg-[#75B339] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                'Import Recipe'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Ingredient Matching Modal */}
      {scrapedData && (
        <IngredientMatchingModal
          open={showIngredientModal}
          onClose={handleIngredientModalClose}
          onConfirm={handleIngredientConfirm}
          parsedIngredients={scrapedData.parsedIngredients}
          recipeTitle={scrapedData.title}
        />
      )}
    </Dialog>
  )
}

export default RecipeImportModal
